/**
 * @file toaster.component.ts
 * @description Host component for `ToastService` — renders the current
 * toast queue at fixed top-right of the viewport. Mounted exactly once
 * in `app.html`. Presentational: no logic beyond click→onClick and
 * click-X→dismiss.
 */
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService, type Toast } from '../../services/toast.service';

@Component({
  selector: 'kirigami-toaster',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './toaster.component.html',
  styleUrl: './toaster.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToasterComponent {
  readonly #service = inject(ToastService);
  readonly toasts = this.#service.toasts;

  trackById(_: number, toast: Toast): string {
    return toast.id;
  }

  onBodyClick(toast: Toast): void {
    toast.onClick?.();
  }

  onDismiss(toast: Toast, event: Event): void {
    event.stopPropagation();
    this.#service.dismiss(toast.id);
  }

  /** Pointer or keyboard focus entered the toaster: hold the timers. */
  onInteractionStart(): void {
    this.#service.pauseTimers();
  }

  /** Interaction ended: let the remaining toasts time out again. */
  onInteractionEnd(): void {
    this.#service.resumeTimers();
  }
}
